import React, { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronUp, Coins, Shield, Wallet, Radio, Mic2, HandCoins, Trophy, MessageCircle, Users, Key } from 'lucide-react';
import { BCHLogo } from '../components/Layout';

interface FAQItem {
  question: string;
  answer: string;
  icon?: React.ReactNode;
}

const FAQ_SECTIONS: { title: string; icon: React.ReactNode; items: FAQItem[] }[] = [
  {
    title: 'Getting Started',
    icon: <HelpCircle className="w-4 h-4 text-primary" />,
    items: [
      {
        question: 'What is BCHNostr?',
        answer: 'BCHNostr is a decentralized social network built on the Nostr protocol with deep Bitcoin Cash (BCH) integration. You can post, follow people, tip with BCH, join audio spaces, watch live streams, and more — all without a central server controlling your data.',
      },
      {
        question: 'How do I create an account?',
        answer: 'You can log in two ways:\n\n1. Browser Extension (recommended) — Install nos2x or Alby, which manages your Nostr keys securely.\n\n2. Private Key (nsec) — Paste your nsec1... key directly. Your key is stored in your browser\'s localStorage.\n\nThere\'s no email, no phone number, no central signup. Your identity is your cryptographic keypair.',
      },
      {
        question: 'What is Nostr?',
        answer: 'Nostr (Notes and Other Stuff Transmitted by Relays) is a simple, open protocol for decentralized social networking. Your posts are signed with your private key and distributed across relay servers. No single company controls it — if one relay goes down, your content lives on others.',
      },
      {
        question: 'Is my data private?',
        answer: 'Posts on Nostr are public by default — anyone can read them on any relay. Direct messages (DMs) are encrypted end-to-end using NIP-04, so only you and the recipient can read them. Your private key (nsec) should never be shared with anyone.',
      },
    ],
  },
  {
    title: 'BCH Wallet & Tipping',
    icon: <Wallet className="w-4 h-4 text-primary" />,
    items: [
      {
        question: 'How does the in-app wallet work?',
        answer: 'When you log in, BCHNostr automatically creates a Bitcoin Cash wallet for you using mainnet-js. Your wallet\'s private key (WIF) is stored locally and also backed up to Nostr relays encrypted with NIP-44, so you can recover it on any device by logging in with the same Nostr key.',
      },
      {
        question: 'How do I fund my wallet?',
        answer: 'Go to Settings > Wallet to see your deposit address and QR code. Send BCH from any wallet (Electron Cash, Bitcoin.com, etc.) to that address. Your balance updates automatically.',
      },
      {
        question: 'How do I tip someone?',
        answer: 'Click the BCH coin icon on any post. Choose an amount (starting from $0.01), and the tip is sent instantly from your in-app wallet. The recipient needs a BCH address set in their profile to receive tips.',
      },
      {
        question: 'How do I set my BCH address to receive tips?',
        answer: 'Go to Settings > Profile and enter your BCH address in the "BCH Address" field. This publishes a special Nostr event (kind 30078) that other users\' apps read when they want to tip you.',
      },
      {
        question: 'What is WizardConnect?',
        answer: 'WizardConnect lets you pair an external BCH wallet (like Electron Cash) with BCHNostr via QR code scanning. Once paired, you can sign transactions directly from your external wallet. Find it in Settings > Wallet.',
      },
    ],
  },
  {
    title: 'Platform Fee',
    icon: <Coins className="w-4 h-4 text-primary" />,
    items: [
      {
        question: 'What is the platform fee?',
        answer: 'BCHNostr charges a small platform fee on tips and payments made through the app. This is how we sustain development — no ads, no data selling, no venture capital.',
      },
      {
        question: 'How is the fee collected?',
        answer: 'A platform fee is included as a second output in the same Bitcoin Cash transaction — so the tip and fee happen atomically in one tx. The fee helps keep BCHNostr running.',
      },
      {
        question: 'Where does the fee go?',
        answer: 'All fees go to: bitcoincash:qzfczm3kc9j8aw0pjm5azym8s8xg07ssru5yr5qepj\n\nThis is fully transparent and verifiable on the BCH blockchain. You can check the address on any block explorer.',
      },
      {
        question: 'Can I verify the fee on-chain?',
        answer: 'Yes. Every tip transaction with a fee has two outputs — one to the recipient and one to the platform address. You can verify this on any BCH block explorer (like bchexplorer.cash) by looking at the transaction ID shown after each tip.',
      },
    ],
  },
  {
    title: 'Features',
    icon: <Radio className="w-4 h-4 text-primary" />,
    items: [
      {
        question: 'What are BCH Rooms?',
        answer: 'BCH Rooms are live audio rooms that run entirely between the people in them — audio is peer-to-peer, so no server ever hears or stores it. Start a room in one click and it gets announced on Nostr (NIP-53) so anyone can discover and join. You get an avatar grid showing who is talking, mute, raise-hand, text chat, and BCH tips for the host. Because there is no audio server, rooms work best with a small group; very large rooms can strain a peer-to-peer connection, and some restrictive mobile or office networks may block it. Find them under BCH Rooms.',
      },
      {
        question: 'How does Live Streaming work?',
        answer: 'BCH Live uses the Nostr NIP-53 protocol. Stream from any platform you like — Shosho (easiest, just download the app), OBS + any RTMP server, Cloudflare Stream, Livepeer, Owncast, etc. Your streaming app publishes a live event to Nostr, and it shows up automatically on BCH Nostr. Verified users can also use the Creator Studio to paste an HLS URL and announce their stream. Tag #bch or #bitcoincash so BCH fans find you!',
      },

      {
        question: 'What are Polls?',
        answer: 'Polls use NIP-88 (kind 1068 for creating polls, kind 1018 for voting). You can create single-choice polls with multiple options and a time limit. Votes are one-per-pubkey and results update in real-time.',
      },
    ],
  },
  {
    title: 'Security & Privacy',
    icon: <Key className="w-4 h-4 text-primary" />,
    items: [
      {
        question: 'Is my nsec key safe in the browser?',
        answer: 'When you log in with nsec, your private key is stored in localStorage. This is convenient but NOT as secure as using a browser extension like nos2x or Alby, which keeps your key isolated. We recommend extensions for accounts with significant funds or followers. Never enter your nsec on untrusted websites.',
      },
      {
        question: 'Can BCHNostr access my BCH funds?',
        answer: 'Your in-app wallet\'s private key (WIF) is stored locally and encrypted on Nostr. Only you can decrypt it with your Nostr key. BCHNostr never sends your private keys to any server. All transactions are signed locally in your browser.',
      },
      {
        question: 'What happens if I clear my browser data?',
        answer: 'Your Nostr identity is safe — just log in again with your extension or nsec. Your BCH wallet WIF is backed up to Nostr (NIP-44 encrypted), so it will be restored automatically when you log in on any device. Accumulated platform fees below the dust limit would be lost (a few sats at most).',
      },
      {
        question: 'Can BCHNostr be censored or shut down?',
        answer: 'BCHNostr is a static website that connects to decentralized Nostr relays and the BCH blockchain. It can be hosted anywhere — on a Raspberry Pi, IPFS, Tor hidden service, or any web server. Your data lives on Nostr relays and the BCH blockchain, not on our servers. Even if this website goes down, your posts, identity, and funds remain accessible.',
      },
    ],
  },
];

export default function FAQPage() {
  return (
    <div>
      <div className="sticky top-0 lg:top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <h1 className="text-lg font-bold flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-primary" /> FAQ
        </h1>
        <p className="text-xs text-muted-foreground mt-0.5">Everything you need to know about BCHNostr</p>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-6">
        {FAQ_SECTIONS.map(section => (
          <div key={section.title}>
            <h2 className="text-sm font-semibold flex items-center gap-2 mb-3 text-primary">
              {section.icon} {section.title}
            </h2>
            <div className="space-y-2">
              {section.items.map(item => (
                <FAQAccordion key={item.question} item={item} />
              ))}
            </div>
          </div>
        ))}

        {/* Contact */}
        <div className="glass-card text-center">
          <p className="text-sm font-medium mb-1">Still have questions?</p>
          <p className="text-xs text-muted-foreground">
            Post with <span className="text-primary font-medium">#BCHNostr</span> on Nostr and the community will help you out.
          </p>
        </div>
      </div>
    </div>
  );
}

function FAQAccordion({ item }: { item: FAQItem }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="glass-card !p-0 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/30 transition-colors"
      >
        <span className="text-sm font-medium pr-4">{item.question}</span>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
      </button>
      {open && (
        <div className="px-4 pb-3 border-t border-border pt-3">
          <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-line">{item.answer}</p>
        </div>
      )}
    </div>
  );
}

